import { useMatch, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Box,
  CircularProgress,
  ImageList,
  ImageListItem,
  Button,
  Dialog,
  IconButton,
  DialogContent,
  DialogTitle,
  DialogActions,
  CardContent,
  Chip,
} from "@mui/material";
import PersonOutline from "@iconify/icons-eva/person-outline";
import AlertCircleFill from "@iconify/icons-eva/alert-circle-fill";
import AlertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import DeleteIcon from "@iconify/icons-eva/minus-fill";
import AddIcon from "@iconify/icons-eva/plus-fill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { is } from "src/util/is";
import { BaseTable } from "../BaseTable/BaseTable";
import {
  teamsFormTabs,
  getTeamDefaultValues,
  teamMembersColumns,
  possibleBusinessTypes,
  initialPayoutAccountObj,
  initialFinanceConfigObj,
  subscriptionDetailsColumns,
  teamFieldToTab,
  teamTabOrder,
  teamTabLabels,
} from "./teams.constants";
import { useTabbedFormErrors } from "src/hooks/useTabbedFormErrors.hook";
import { Form, FormikProvider, useFormik } from "formik";
import {
  useGetDetail,
  useMutate,
  usePaymentDetailsOfCompany,
} from "src/hooks/useFetch.hook";
import { teamUrl, teamUrlId, teamChargerLicenseStatusUrl, teamChargerLicenseUrl } from "src/react-query/endPoints";
import { useAccess } from "src/hooks/useAccess.hook";
import { Loader } from "../Loader";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError } from "src/react-query/fetchWithError";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { selector } from "src/react-query/select";
import enums, { FEATURE } from "src/util/enums";
import Address from "src/common/components/Address";
import { teamFormValidationSchema } from "src/util/validationSchemas";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";
import { Icon } from "@iconify/react";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { useDispatch, useSelector } from "react-redux";
import { TeamUserModal } from "./TeamUserModal";
import { alertSlice } from "src/store/alert/alert.slice";
import { isObjectWithNonNullValues } from "src/utils/utility";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import TeamFinanceForm from "./TeamFinanceForm";
import TeamBillingInfoSection from "./TeamBillingInfoSection";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useMutation } from "@tanstack/react-query";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import {
  issueImgUpload,
  issueSubmission,
} from "../ServiceRequest/serviceRequest.hook";
import ImgPreviewModal from "src/components/ImagePreviewModal/ImgPreviewModal";
import { GridCloseIcon } from "@mui/x-data-grid";
import sanitizeValues from "src/utils/sanitizeValues";
import { isValidImageType, MAX_IMAGE_SIZE_MB } from "src/utils/fileTypes";
import SubscriptionDetailsModal from "./SubscriptionDetailsModal";
import BulkNotificationModal from "./BulkNotificationModal";

// import TeamPayoutInvoices from "./TeamPayoutInvoices";
import TeamPayoutScheduleTab from "./TeamPayoutScheduleTab";
import CompanyEnableFeatures from "../company/CompanyEnableFeatures";
import Communicate from "../company/Communicate";
import TeamPayoutAccountEditModal from "./TeamPayoutAccountEditModal";
import { communicationDefaultValue } from "../company/company.constants";
import { ChargerLicenseModal } from "src/common/components/ChargerLicenseModal";
import { RenewalChargerTable } from "src/common/components/RenewalChargerTable";

const LICENSE_TABS = [
  { value: "expiredLicenses", label: "Expired Licenses" },
  { value: "upcomingLicensesExpiry", label: "Upcoming Licenses Expiry" },
];

export const TeamForm = () => {
  // const [chargemilesPayoutIdDisabled,setChargemilesPayoutIdDisabled] = useState(true);
  const { teamId: parentCompanyId } = useSelectedTeam();
  const [selectedTab, setSelectedTab] = useState("general");
  const [addPayoutAccounts, setAddPayoutAccounts] = useState(false);
  const { id } = useParams();
  const { open: modalOpen } = useSelector(({ dialog }) => dialog);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;
  const isCreateTeam = useMatch("/team/new");
  const hasReadOnlyAccess = useAccess(FEATURE.TEAM, "R");
  const canEditFinanceConfig = useAccess(FEATURE.FINANCE, "FA");
  const canViewFinanceConfig = useAccess(FEATURE.FINANCE, "V");
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const [uploadedImgUrl, setUploadedImageUrl] = useState(false);
  const imageUploadInputRef = useRef();
  const imgUpload = useMutation(issueImgUpload);
  const submission = useMutation(issueSubmission);
  const [photoModal, setPhotoModal] = useState({ open: false, src: "" });
  const [payoutAccountModalState, setPayoutAccountModalState] = useState({
    open: false,
    index: null,
  });

  const isReadOnly = hasReadOnlyAccess && !isCreateTeam;
  const financeFormPermissions = { isReadOnly: isReadOnly || !canEditFinanceConfig };

  const [teamUsersData, setTeamUsers] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedSubscriptionData, setSelectedSubscriptionData] = useState(null);
  // --- License renewal flow state ---
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [licenseSelectedTab, setLicenseSelectedTab] = useState("expiredLicenses");
  const [selectedRenewalChargers, setSelectedRenewalChargers] = useState([]);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [chargerLicensePurchaseModalOpen, setChargerLicensePurchaseModalOpen] = useState(false);
  const [renewalNotificationModalOpen, setRenewalNotificationModalOpen] = useState(false);
  const [renewalNotificationLicenseIds, setRenewalNotificationLicenseIds] = useState([]);

  const {
    data: teamUsers,
    isFetching: isUserFetching,
    refetch: refetchMembers,
    isRefetching: isRefetchingMembers,
  } = useGetDetail("usersByTeam", { id });

  const { data: company } = useGetDetail("company", { id: parentCompanyId });
  const [taxLabel, setTaxLabel] = useState(
    (company && company?.taxBillingConfig?.taxLabel) || "Tax"
  );
  const user = useSelector((state) => state.auth.user);
  const userCompany = user?.company;

  // --- License renewal flow: data ---
  const { data: licenseDetailsData, isFetching: licenseDetailsFetching } = useQuery(
    queryKeys.licensesDetails(id, {}),
    fetchWithError,
    { enabled: licenseModalOpen && !is.empty(id), select: selector }
  );
  const { data: { charger_license_payment_config } = {} } = useQuery(
    queryKeys.chargerLicenseConfig(id),
    fetchWithError,
    { enabled: !is.empty(id), select: selector }
  );
  const expiredLicenses = licenseDetailsData?.chargersWithoutLicenseCount || 0;
  const upcomingExpiringLicenses = licenseDetailsData?.chargersWithUpcomingLicenseExpiryCount || 0;
  const chargersWithoutLicense = licenseDetailsData?.chargersWithoutLicense || [];
  const chargersWithUpcomingLicenseExpiry = licenseDetailsData?.chargersWithUpcomingLicenseExpiry || [];

  const { mutate: initiateLicenseSale } = useMutate("chargerLicenseConfig");

  // --- License renewal flow: handlers ---
  const handleLicenseModalOpen = () => setLicenseModalOpen(true);
  const handleLicenseModalClose = () => setLicenseModalOpen(false);
  const handleLicenseCancel = () => { setLicenseModalOpen(false); setSelectedRenewalChargers([]); };
  const handleLicenseTabChange = (_, tab) => { setLicenseSelectedTab(tab); setSelectedRenewalChargers([]); };

  const handleLicensePurchase = (payload) => {
    const isRenewal = (payload.chargersToRenew?.length ?? 0) > 0;
    const baseUrl = teamChargerLicenseUrl(id);
    const url = isRenewal ? `${baseUrl}/renew` : `${baseUrl}/add`;

    const body = {
      ...payload,
      ...(isRenewal && {
        chargersToRenew: payload.chargersToRenew.map(({ charge_box_pk, charger_type }) => ({
          charge_box_pk: String(charge_box_pk),
          charger_type,
        })),
      }),
    };

    initiateLicenseSale(
      { url, method: 'POST', body },
      {
        onSuccess: () => {
          setChargerLicensePurchaseModalOpen(false);
          setRenewModalOpen(false);
          dispatch(setAlert({
            open: true,
            message: isRenewal ? 'Charger licenses renewed successfully.' : 'Charger licenses added successfully.',
            severity: enums.SEVERITY.SUCCESS,
          }));
          queryClient.invalidateQueries(queryKeys.licensesDetails(id, {}));
          if (!isRenewal) {
            queryClient.invalidateQueries(queryKeys.team(id));
          }
        },
        onError: (error) => {
          dispatch(setAlert({
            open: true,
            message: error?.message || 'Failed to process license payment. Please try again.',
            severity: enums.SEVERITY.ERROR,
          }));
        },
      }
    );
  };

  const isParentCompanyLogin = userCompany?.companyType === "P";
  const isGPCLogin = userCompany?.companyType === "GP";

  const tabsToShow = useMemo(() => {
    let updatedTeamsFormTab = [...teamsFormTabs];
    if (isParentCompanyLogin) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "finance" && tab.value !== "payout schedule"
      );
    }

    if (!canViewFinanceConfig) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "finance"
      );
    }

    if (!canEditFinanceConfig) {
      updatedTeamsFormTab = updatedTeamsFormTab.filter(
        (tab) => tab.value !== "payout schedule"
      );
    }

    if (id) {
      return updatedTeamsFormTab;
    }
    return updatedTeamsFormTab.filter((tab) => tab.value !== "members" && tab.value !== "subscription" && tab.value !== "payout schedule");
  }, [id, isParentCompanyLogin, canEditFinanceConfig, canViewFinanceConfig]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!isValidImageType(file.type)) {
      dispatch(
        setAlert({
          open: true,
          message: `Only JPEG, JPG, PNG, WEBP or SVG images are allowed.`,
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      dispatch(
        setAlert({
          open: true,
          message: `File size exceeds ${MAX_IMAGE_SIZE_MB} MB.`,
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    const updatedImagePreview = { ...imagePreview, open: true, value: file };
    let updatedAttachmentObj = {
      ...values.attachment,
      file,
      isUploading: true,
    };
    formik.setFieldValue("attachment", updatedAttachmentObj);
    setImagePreview(updatedImagePreview);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImageHandler = async (file) => {
    if (file.size > 1048576) {
      dispatch(
        setAlert({
          open: true,
          message: "File size should not exceed 1 MB.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    // Validate image dimensions
    const imageDimensions = await getImageDimensions(file);
    if (imageDimensions.width > 80 || imageDimensions.height > 80) {
      dispatch(
        setAlert({
          open: true,
          message: "Image dimensions should not exceed 80 x 80 pixels.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    imgUpload.mutate(
      {
        image: file,
        folder: "logo",
      },
      {
        onSuccess: (response) => {
          // console.log(response)
          setImageUploadPending(false);
          const updatedAttachmentObj = {
            ...values.attachment,
            url: response.data.data.url,
          };
          formik.setFieldValue("attachment", updatedAttachmentObj);
        },
      }
    );
    closePreviewModal();
    setImageUploadPending(true);
  };

  const cancelImageUpload = (currentSavedImg) => {
    if (currentSavedImg) {
      formik.setFieldValue("attachment", { url: currentSavedImg });
    } else formik.setFieldValue("attachment", {});
    closePreviewModal();
  };

  const closePreviewModal = () =>
    setImagePreview({ ...imagePreview, open: false });

  const [imagePreview, setImagePreview] = useState({
    open: false,
    value: "",
    imagePreviewBtns: [
      {
        btnLabel: "Upload",
        action: uploadImageHandler,
        params: true,
      },
      {
        btnLabel: "Cancel",
        action: cancelImageUpload,
        params: true,
      },
    ],
  });

  const { data: paymentDetails, isFetching: isPaymentDetailsFetching } =
    usePaymentDetailsOfCompany(canViewFinanceConfig);
  const { data: teams, isFetching } = useQuery(
    queryKeys.team(id),
    fetchWithError,
    { enabled: !is.empty(id), select: selector }
  );

  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutate("team");

  const handleDisableSubscription = useCallback((licensePk) => {
    if (!licensePk) {
      console.error('licensePk is undefined or null');
      dispatch(
        setAlert({
          open: true,
          message: "License ID is missing. Please refresh the page and try again.",
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    mutate(
      {
        url: teamChargerLicenseStatusUrl(id),
        method: "PATCH",
        body: { license: licensePk }
      },
      {
        onSuccess: () => {
          // Refetch team data which includes subscription data
          queryClient.invalidateQueries(queryKeys.team(id));
          dispatch(
            setAlert({
              open: true,
              message: "Subscription status updated successfully.",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
        },
        onError: (error) => {
          console.error("Failed to update subscription status:", error);
          dispatch(
            setAlert({
              open: true,
              message: "Failed to update subscription status. Please try again.",
              severity: enums.SEVERITY.ERROR,
            })
          );
        }
      }
    );
  }, [mutate, id, queryClient, dispatch, setAlert]);



  const onSubmit = async (teamData, { setSubmitting }) => {
    const sanitizable = sanitizeValues(teamData);
    const mutateObject = {
      url: teamUrl,
      method: "POST",
    };
    const {
      name,
      description,
      operatorName,
      legal_name,
      active,
      city,
      country,
      state,
      street,
      houseNumber,
      addressLine3,
      zipCode,
      address,
      locationLongitude,
      locationLatitude,
      payoutAccounts,
      financeConfig,
      attachment,
      advance_settings,
      communication,
      billingInfo
    } = sanitizable;

    if (is.empty(locationLatitude) || is.empty(locationLongitude)) {
      dispatch(
        setAlert({
          open: true,
          message:
            "Please specify an address, lattitude and longitude cant be empty",
          severity: enums.SEVERITY.ERROR,
        })
      );
      setSubmitting(false);
      return;
    }


    const finalPayoutAccounts = payoutAccounts ? payoutAccounts.reduce((finalPayoutAccounts, payoutAccount) => {
      const noNullValues = isObjectWithNonNullValues(payoutAccount, [
        "gst",
      ]);

      if (noNullValues) {
        const {
          business_name,
          business_type,
          beneficiary_name,
          email,
          phone,
          account_no,
          ifsc_code,
          gst,
          payoutDetailId,
          tax_percentage,
          payout_percentage,
          account_type,
          is_default,
        } = payoutAccount;
        const updatedPayoutAccount = {
          business_name: business_name.trim(),
          business_type: business_type.trim(),
          beneficiary_name: beneficiary_name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          account_no: account_no.trim(),
          ifsc_code: ifsc_code.trim(),
          gst: gst.trim(),
          account_type: account_type.trim(),
          payout_percentage,
          tax_percentage,
          payoutDetailId: payoutDetailId ?? null,
          is_default,
        };

        finalPayoutAccounts = [
          ...finalPayoutAccounts,
          updatedPayoutAccount,
        ];
      }
      return finalPayoutAccounts;
    }, []) : [];
    const teamReqBody = {
      name,
      description,
      operatorName,
      legal_name,
      active,
      parentCompanyId,
      address: {
        ...address,
        city,
        country,
        zipCode,
        state,
        street,
        houseNumber,
        addressLine1: street,
        addressLine2: houseNumber || "",
        addressLine3: addressLine3 || "",
        lat: locationLatitude,
        lng: locationLongitude,
      },
      attachment,
      enable_license_payment: values.advance_settings?.enable_license_payment || !1,
      erp_company_id: null,
      payoutAccounts: finalPayoutAccounts,
      financeConfig,
      communication,
      billingInfo
    };

    if (!canEditFinanceConfig) {
      delete teamReqBody.financeConfig;
    }

    if (canEditFinanceConfig && isParentCompanyLogin) {
      delete teamReqBody.financeConfig;
    }

    const { erp_company_id, erp_company_name } = advance_settings;
    if (erp_company_id) {
      teamReqBody.erp_company_id = erp_company_id;
    }

    if (erp_company_name) {
      teamReqBody.erp_company_name = erp_company_name
    }

    if (!is.nullOrUndefined(id) && !isCreateTeam) {
      mutateObject.method = "PUT";
      mutateObject.url = teamUrlId(id);
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: teamReqBody,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const profileRefresh = async () => {
    try {
      await queryClient.fetchQuery({
        queryKey: queryKeys.profile(),
        queryFn: fetchWithError,
        staleTime: 0
      });
      
    } catch (error) {
      console.error("Error calling profile API:", error);
    }
  }

  const onSuccess = async () => {
    await profileRefresh();
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "",
        tabOpened: "",
      })
    );
    navigate("/team");
  };

  const formik = useFormik({
    initialValues: {
      ...getTeamDefaultValues(),
      enable_license_payment: company?.enable_license_payment ?? false,
      ...(canViewFinanceConfig
        ? {}
        : {
          financeConfig: undefined,
          payoutAccounts: [initialPayoutAccountObj],
        }),
    },
    validationSchema: teamFormValidationSchema,
    onSubmit,
  });

  const {
    errors,
    values,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    handleChange,
  } = formik;

  const { handleSaveClick } = useTabbedFormErrors({
    formik,
    fieldToTab: teamFieldToTab,
    tabOrder: teamTabOrder,
    tabLabels: teamTabLabels,
    setSelectedTab,
  });

  useEffect(() => {
    // When company license flag changes to true, allow team to toggle
    if (
      company &&
      company.enable_license_payment === true &&
      values.enable_license_payment === false
    ) {
      setFieldValue("enable_license_payment", false);
    }
  }, [company?.enable_license_payment, setFieldValue]);

  useEffect(() => {
    paymentDetails && setTaxLabel(company?.taxBillingConfig?.taxLabel || "Tax");
  }, [paymentDetails]);

  useEffect(() => {
    !is.empty(teamUsers) && setTeamUsers(teamUsers);
  }, [teamUsers]);

  // Process real subscription data from team details API
  useEffect(() => {
    if (selectedTab === 'subscription' && !is.empty(teams)) {
      setIsSubscriptionLoading(true);

      const [team] = teams;
      if (team && team.chargerLicenses) {
        const subscriptionData = team.chargerLicenses.map((license, index) => ({
          id: license?.licensePk || index + 1,
          licensePk: license?.licensePk,
          licenseId: license?.licenseId,
          status: license?.status,
          teamName: team?.name,
          chargerId: license?.chargeBoxId,
          chargerCreationDate: license?.createdAt,
          chargerSubscriptionExpiryDate: license?.expiry,
          duration: license?.duration,
          lastRenewalDate: license?.createdAt,
          final_expiry: license?.final_expiry,
          contractSummary: license?.summary,
          remarks: license?.remarks,
          chargerType: license?.chargerType,
        }));

        setSubscriptionData(subscriptionData);
      } else {
        setSubscriptionData([]);
      }
      setIsSubscriptionLoading(false);
    }
  }, [selectedTab, teams]);

  useEffect(() => {
    if (!is.empty(teams)) {
      const [team] = teams;
      const { address, payoutAccounts, financeConfig, url, erpCompanyId, erpCompanyName } = team;
      const { lat, lng } = address;

      const newValues = {
        ...team,
        ...address,
        locationLatitude: lat,
        locationLongitude: lng,
        attachment: { url: url },
        advance_settings: {
          erp_company_id: null,
          erp_company_name: null
        }
      };

      if (canViewFinanceConfig) {
        newValues.financeConfig = is.empty(financeConfig)
          ? initialFinanceConfigObj
          : financeConfig;
      } else {
        delete newValues.financeConfig;
      }

      // Always populate payoutAccounts regardless of finance access
      newValues.payoutAccounts = is.empty(payoutAccounts)
        ? [initialPayoutAccountObj]
        : payoutAccounts;

      if (erpCompanyId && erpCompanyName) {
        newValues.advance_settings.erp_company_id = erpCompanyId;
        newValues.advance_settings.erp_company_name = erpCompanyName;
      }

      if (!newValues.communication) {
        newValues.communication = communicationDefaultValue().communication;
      }

      newValues.billingInfo = {
        ...getTeamDefaultValues().billingInfo,
        ...(team.billingInfo || {}),
      };
      // Older teams were saved before the GST toggle existed, so fall back to
      // "has GST" only when a GSTIN was actually stored.
      if (typeof team.billingInfo?.hasGst !== "boolean") {
        newValues.billingInfo.hasGst = Boolean(team.billingInfo?.gstin);
      }

      resetForm({ values: newValues });
    }
  }, [teams, resetForm, canViewFinanceConfig]);

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };

  const handleManageUsersOpen = useCallback(() => {
    dispatch(dialogSlice.actions.setDialog({ open: true, id }));
  }, [dispatch, id]);

  const handleMangageUsersClose = useCallback(() => {
    refetchMembers();
    dispatch(dialogSlice.actions.setDialog({ open: false, id: null }));
  }, [dispatch, refetchMembers]);

  const handleSubscriptionDetailsOpen = useCallback((subscriptionRow) => {
    setSelectedSubscriptionData(subscriptionRow);
    setSubscriptionModalOpen(true);
  }, []);

  const handleSubscriptionDetailsClose = useCallback(() => {
    setSubscriptionModalOpen(false);
    setSelectedSubscriptionData(null);
  }, []);

  const handleBankAccountDelete = useCallback(
    (index) => {
      const numberOfBankDetails = (values.payoutAccounts || []).length;
      if (numberOfBankDetails === 1) {
        dispatch(
          setAlert({
            open: true,
            message: "At least one payout account shall be defined",
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }
      const updatedBankDetailsFields = (values.payoutAccounts || []).filter(
        (_, ind) => index !== ind
      );
      setFieldValue("payoutAccounts", updatedBankDetailsFields);
      // setFieldValue("disabled",true);
      return;
    },
    [values.payoutAccounts, setFieldValue, dispatch, setAlert]
  );

  const handleBankAccountAddition = useCallback(() => {
    const updatedBankDetailsFields = [
      ...(values.payoutAccounts || []),
      initialPayoutAccountObj,
    ];
    setFieldValue("payoutAccounts", updatedBankDetailsFields);
    return;
  }, [values.payoutAccounts, setFieldValue]);

  const handlePayoutAccountEditOpen = useCallback((index) => {
    setPayoutAccountModalState({ open: true, index });
  }, []);

  const handlePayoutAccountEditClose = useCallback(() => {
    setPayoutAccountModalState({ open: false, index: null });
  }, []);

  const handlePayoutAccountEditSave = useCallback(
    (updatedAccountValues) => {
      if (is.nullOrUndefined(payoutAccountModalState.index)) {
        handlePayoutAccountEditClose();
        return;
      }

      const sanitizedAccount = Object.entries(updatedAccountValues || {}).reduce(
        (acc, [key, value]) => {
          acc[key] = typeof value === "string" ? value.trim() : value;
          return acc;
        },
        {}
      );

      const updatedAccounts = (values.payoutAccounts || []).map((account, idx) => {
        if (idx === payoutAccountModalState.index) {
          return {
            ...account,
            ...sanitizedAccount,
          };
        }
        // If the updated account is being set as default, unset all others
        if (sanitizedAccount.is_default === 1) {
          return {
            ...account,
            is_default: 0,
          };
        }
        return account;
      });

      setFieldValue("payoutAccounts", updatedAccounts);
      handlePayoutAccountEditClose();
    },
    [
      values.payoutAccounts,
      setFieldValue,
      payoutAccountModalState.index,
      handlePayoutAccountEditClose,
    ]
  );

  useEffect(() => {
    if (isCreateTeam && company) {
      formik.setFieldValue(
        "enable_license_payment",
        !!company.enable_license_payment
      );
    }
  }, [company, isCreateTeam]);

  // Set team name in store for the BackButton display
  useEffect(() => {
    if (!is.empty(teams) && !isCreateTeam) {
      const [team] = teams;
      if (team && team.name) {
        dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
          detailsId: team.name,
          tabOpenedFor: "team",
          tabOpened: selectedTab
        }));
      }
    }
  }, [teams, isCreateTeam, selectedTab, dispatch]);

  const payoutAccountBeingEdited =
    typeof payoutAccountModalState.index === "number"
      ? values?.payoutAccounts?.[payoutAccountModalState.index]
      : null;

  if (isFetching) {
    return <Loader />;
  }

  if (!isCreateTeam && is.empty(teams)) {
    navigate("/team");
    return null;
  }

  const allPayoutAccounts = values.payoutAccounts || [];
  const payoutHasNoDefault = !allPayoutAccounts.some(acc => acc.is_default === 1);
  const payoutDefaultAccount = allPayoutAccounts.find(acc => acc.is_default === 1);
  const payoutDefaultMissingGst = payoutDefaultAccount && !payoutDefaultAccount.gst?.trim();

  return (
    <>
      {(payoutHasNoDefault || payoutDefaultMissingGst) && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {payoutHasNoDefault && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 50%, #f39c12 100%)',
                color: '#2d3436',
                boxShadow: '0 4px 16px rgba(243, 156, 18, 0.25)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1.5, p: 1, borderRadius: 1.5, backgroundColor: 'rgba(45, 52, 54, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon={AlertTriangleFill} width={24} height={24} style={{ color: '#2d3436' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.25 }}>
                    No Default Payout Account
                  </Typography>
                  <Typography variant="body2">
                    No default payout account has been set. Please mark one account as default.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          {payoutDefaultMissingGst && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #a8edea 0%, #74b9ff 50%, #0984e3 100%)',
                color: '#2d3436',
                boxShadow: '0 4px 16px rgba(9, 132, 227, 0.25)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1.5, p: 1, borderRadius: 1.5, backgroundColor: 'rgba(45, 52, 54, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon={AlertCircleFill} width={24} height={24} style={{ color: '#2d3436' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.25 }}>
                    GST Number Missing
                  </Typography>
                  <Typography variant="body2">
                    The default payout account is missing a GST number. Please fill in the GST details.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      )}
      <TabContext value={selectedTab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItem="center"
          sx={{ width: "100%" }}
        >
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={tabsToShow.length}
            onChange={handleTabChange}
          >
            {tabsToShow.map((tab) => (
              <RootTabItemStyles
                key={tab.value}
                value={tab.value}
                label={tab.label}
              />
            ))}
          </RootTabStyle>
        </Stack>

        <Card sx={{ p: 2 }}>
          <FormikProvider value={formik}>
            <Form
              autoComplete="off"
              id="team"
              noValidate
              onSubmit={handleSubmit}
            >
              <TabPanel value="general">
                <Grid container spacing={3} className="cms-grid-mb">
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="name"
                        label="Team Name"
                        size="small"
                        variant="outlined"
                        {...getFieldProps("name")}
                        disabled={isReadOnly}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="legal_name"
                        label="Legal Name"
                        size="small"
                        variant="outlined"
                        {...getFieldProps("legal_name")}
                        disabled={isReadOnly}
                        error={Boolean(touched.legal_name && errors.legal_name)}
                        helperText={touched.legal_name && errors.legal_name}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="operator-name"
                        label="Operator Name"
                        size="small"
                        variant="outlined"
                        {...getFieldProps("operatorName")}
                        disabled={isReadOnly}
                        error={Boolean(touched.operatorName && errors.operatorName)}
                        helperText={touched.operatorName && errors.operatorName}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="demo-simple-select-label">
                        {" "}
                        Status{" "}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Status"
                        defaultValue={values.active}
                        disabled={isReadOnly}
                        {...getFieldProps("active")}
                      >
                        <MenuItem value={"Y"}>Active</MenuItem>
                        <MenuItem value={"N"}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControl fullWidth size="medium">
                      <TextField
                        multiline
                        rows={6}
                        variant="outlined"
                        id="description"
                        label="Description"
                        {...getFieldProps("description")}
                        error={Boolean(
                          touched.description && errors.description
                        )}
                        helperText={touched.description && errors.description}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "10px" }}
                      >
                        {imageUploadPending ? (
                          <Box
                            sx={{
                              height: "170px",
                              width: "250px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : values.attachment &&
                          is.empty(values.attachment.url) ? (
                          <TextField
                            label="Upload WebApp icon"
                            value={!imageUploadPending ? "" : "LOADING...."}
                            placeholder={"No file chosen"}
                            onChange={handleChange}
                            helperText={`This Image will show as icon on WebApp.`}
                            disabled
                          />
                        ) : (
                          <div>
                            <Typography variant="body2" color="text.secondary">
                              Upload WebApp icon
                            </Typography>
                            {values?.attachment?.url && (
                              <Card
                                style={{
                                  border: "1px solid #ddd",
                                  marginBottom: 10,
                                  boxShadow: "none",
                                  width: "400px",
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    placeContent: "center",
                                  }}
                                >
                                  <ImageList cols={1}>
                                    <ImageListItem
                                      key={`${values?.attachment?.url}-key`}
                                    >
                                      <img
                                        src={values?.attachment?.url}
                                        alt={"attachment"}
                                        style={{
                                          maxHeight: "100px",
                                          maxWidth: "100px",
                                        }}
                                        loading="eager"
                                        onClick={() => {
                                          setPhotoModal({
                                            open: true,
                                            src: values?.attachment?.url,
                                          });
                                        }}
                                      />
                                    </ImageListItem>
                                  </ImageList>
                                </CardContent>
                              </Card>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              This Image will show as icon on WebApp.
                            </Typography>
                          </div>
                        )}
                        {!isReadOnly && (
                          <LoadingButton
                            variant="contained"
                            onClick={() => imageUploadInputRef.current.click()}
                            loading={imageUploadPending}
                            loadingIndicator="Loading…"
                            endIcon={<Icon icon="uil:image-upload"></Icon>}
                          >
                            Upload
                            <input
                              accept="image/jpeg,image/png,image/jpg"
                              type="file"
                              hidden
                              id="issue-image"
                              ref={imageUploadInputRef}
                              onChange={handleImageUpload}
                            />
                          </LoadingButton>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                  {company?.advance_settings &&
                    <Grid item xs={12}>
                      <CompanyEnableFeatures
                        getFieldProps={getFieldProps}
                        values={values}
                        setFieldValue={setFieldValue}
                        isTeam={true}
                        companyId={company.advance_settings.erp_company_id}
                        parentLicenseStatus={company?.enable_license_payment}
                        showMapping={!!company.advance_settings.erp_company_id}
                      />
                    </Grid>}
                  {!isCreateTeam && values.advance_settings.erp_company_name &&
                    <Grid item xs={12}>
                      <Communicate
                        getFieldProps={getFieldProps}
                        values={values}
                        setFieldValue={setFieldValue}
                        isTeam={true}
                        advanceSettingValue={values}
                        containerStyle={{ padding: 0 }}
                      />
                    </Grid>}
                </Grid>
              </TabPanel>
              <TabPanel value="invoice setting">
                <TeamBillingInfoSection
                  values={values}
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  setFieldValue={setFieldValue}
                  isReadOnly={isReadOnly}
                  company={company}
                />
              </TabPanel>
              <TabPanel value="payout accounts">
                {(isCreateTeam || isGPCLogin || isParentCompanyLogin) && !isReadOnly && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="right"
                    mb={3}
                  >
                    <LoadingButton
                      size="medium"
                      variant="contained"
                      startIcon={<Icon icon={AddIcon}></Icon>}
                      // loading={isSubmitting || isLoading}
                      onClick={handleBankAccountAddition}
                    >
                      Add Account
                    </LoadingButton>
                  </Stack>
                )}
                <Stack>
                  {(values.payoutAccounts || []).map(
                    (payoutAccountObj, index, arr) => {
                      const { business_type, creation, deleteBtn } =
                        payoutAccountObj;

                      // if(disabled) return<></>;

                      return (
                        <Grid
                          container
                          spacing={3}
                          my={3}
                          className="cms-grid-mb"
                          key={`payout-account-${index}`}
                        >
                          <Grid item xs={12} lg={12}>
                            <Stack
                              direction="row"
                              alignItems="baseline"
                              justifyContent="space-between"
                              paddingBottom={2}
                              sx={{ borderBottom: "1px solid grey" }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography>
                                  {`PAYOUT ACCOUNT ${index + 1}`}
                                </Typography>
                                {payoutAccountObj.is_default === 1 && (
                                  <Chip
                                    label="DEFAULT"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                  />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {!creation && !isReadOnly && (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Icon icon="mdi:pencil-outline"></Icon>}
                                    onClick={() => handlePayoutAccountEditOpen(index)}
                                  >
                                    {`Edit PAYOUT ACCOUNT ${index + 1}`}
                                  </LoadingButton>
                                )}
                                {deleteBtn && arr.length > 1 && !isReadOnly && (
                                  <LoadingButton
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    startIcon={<Icon icon={DeleteIcon}></Icon>}
                                    // loading={isSubmitting || isLoading}
                                    onClick={() => handleBankAccountDelete(index)}
                                  >
                                    Delete
                                  </LoadingButton>
                                )}
                              </Stack>
                            </Stack>
                            {/* <Divider variant="middle" /> */}
                          </Grid>
                          {creation && (
                            <Grid item xs={12} lg={12} md={12}>
                              <Typography variant="caption">
                                **Either fill the form completely (excluding{" "}
                                {taxLabel}) or keep form completely empty
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`business-name-${index}`}
                                label="Business / Individual Name"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].business_name`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .business_name &&
                                  errors.payoutAccounts[index].business_name
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].business_name &&
                                  errors.payoutAccounts[index].business_name
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl
                              size="small"
                              fullWidth
                              error={
                                touched &&
                                touched.payoutAccounts &&
                                errors &&
                                errors.payoutAccounts &&
                                touched.payoutAccounts[index] &&
                                errors.payoutAccounts[index] &&
                                touched.payoutAccounts[index].business_type &&
                                errors.payoutAccounts[index].business_type
                              }
                            >
                              <InputLabel
                                id={`business-type-input-label-${index}`}
                              >
                                Business Type
                              </InputLabel>
                              <Select
                                labelId={`business-type-input-label-${index}`}
                                id={`business-type-${index}`}
                                disabled={isReadOnly || !creation}
                                value={business_type}
                                onChange={(e) =>
                                  setFieldValue(
                                    `payoutAccounts[${index}].business_type`,
                                    e.target.value
                                  )
                                }
                                label="Business Type"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].business_type`
                                )}
                              >
                                <MenuItem selected value="None" disabled>
                                  <em>None</em>
                                </MenuItem>
                                {possibleBusinessTypes.map(
                                  ({ label, value, disabled }, index) => {
                                    return (
                                      <MenuItem
                                        key={`transaction-status-${index}-${label}`}
                                        value={value}
                                        disabled={disabled}
                                      >
                                        {label}
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`beneficiary-name-${index}`}
                                label="Beneficiary Name"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].beneficiary_name`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .beneficiary_name &&
                                  errors.payoutAccounts[index]
                                    .beneficiary_name
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .beneficiary_name &&
                                  errors.payoutAccounts[index].beneficiary_name
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`email-${index}`}
                                label="Email"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].email`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].email &&
                                  errors.payoutAccounts[index].email
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].email &&
                                  errors.payoutAccounts[index].email
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`phone-${index}`}
                                label="Phone Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].phone`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].phone &&
                                  errors.payoutAccounts[index].phone
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].phone &&
                                  errors.payoutAccounts[index].phone
                                }
                              />
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`account-no-${index}`}
                                label="Account Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].account_no`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_no &&
                                  errors.payoutAccounts[index].account_no
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_no &&
                                  errors.payoutAccounts[index].account_no
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`account-no-${index}`}
                                label="Account Type"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].account_type`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index]
                                    .account_type &&
                                  errors.payoutAccounts[index].account_type
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].account_type &&
                                  errors.payoutAccounts[index].account_type
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`ifsc-code-${index}`}
                                label="IFSC Code"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].ifsc_code`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].ifsc_code &&
                                  errors.payoutAccounts[index].ifsc_code
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].ifsc_code &&
                                  errors.payoutAccounts[index].ifsc_code
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={4} md={4}>
                            <FormControl fullWidth size="small">
                              <TextField
                                id={`bank-address-${index}`}
                                label="GST Number"
                                size="small"
                                variant="outlined"
                                {...getFieldProps(
                                  `payoutAccounts[${index}].gst`
                                )}
                                disabled={isReadOnly || !creation}
                                error={Boolean(
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].gst &&
                                  errors.payoutAccounts[index].gst
                                )}
                                helperText={
                                  touched &&
                                  touched.payoutAccounts &&
                                  errors &&
                                  errors.payoutAccounts &&
                                  touched.payoutAccounts[index] &&
                                  errors.payoutAccounts[index] &&
                                  touched.payoutAccounts[index].gst &&
                                  errors.payoutAccounts[index].gst
                                }
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      );
                    }
                  )}
                </Stack>
              </TabPanel>
              <TabPanel value="billing address">
                <Address
                  getFieldProps={getFieldProps}
                  setFieldValue={setFieldValue}
                  formValues={values}
                  touched={touched}
                  errors={errors}
                  notDisplayTitle={true}
                  displayedFor="team"
                />
              </TabPanel>
              <TabPanel value="members">
                {!isCreateTeam && (
                  <Card
                    variant="outlined"
                    sx={{ p: 1 }}
                    style={{ marginBottom: "20px", padding: "35px 20px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        margin: "20px",
                      }}
                    >
                      <h2 style={{ margin: "5px" }}>
                        {" "}
                        Team members ({teamUsersData.length}){" "}
                      </h2>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        endIcon={<Icon icon={PersonOutline}></Icon>}
                        disabled={isReadOnly}
                        onClick={handleManageUsersOpen}
                      >
                        Manage members
                      </LoadingButton>
                    </div>
                    <BaseTable
                      rows={teamUsersData ?? []}
                      columns={teamMembersColumns(false, false)}
                      getRowId={(row) => row.userId}
                      loading={isUserFetching || isRefetchingMembers}
                      pageSize={15}
                    />
                  </Card>
                )}
              </TabPanel>
              <TabPanel value="finance">
                {!isPaymentDetailsFetching ? (
                  <TeamFinanceForm
                    formik={formik}
                    permissions={financeFormPermissions}
                    taxLabel={taxLabel}
                    paymentDetails={paymentDetails ?? {}}
                  />
                ) : (
                  <Loader />
                )}
              </TabPanel>
              {/* <TabPanel value="payout invoices">
                <TeamPayoutInvoices />
              </TabPanel> */}
              <TabPanel value="subscription">
                {!isReadOnly && !isCreateTeam && canViewFinanceConfig && !isParentCompanyLogin && (
                  <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setChargerLicensePurchaseModalOpen(true)}
                      startIcon={<Icon icon="eva:shopping-cart-fill" />}
                    >
                      Assign Charger License
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleLicenseModalOpen}
                      startIcon={<Icon icon="mdi:card-account-details-outline" />}
                    >
                      Manage Charger License
                    </Button>
                  </Stack>
                )}

                {!isCreateTeam && (
                  <>
                    <BaseTable
                      rows={subscriptionData ?? []}
                      columns={subscriptionDetailsColumns(
                        handleSubscriptionDetailsOpen,
                        !isParentCompanyLogin,
                        handleDisableSubscription,
                        canEditFinanceConfig
                      )}
                      getRowId={(row) => row.id}
                      loading={isSubscriptionLoading}
                      pageSize={15}
                    />
                  </>
                )}
                {isCreateTeam && (
                  <Card
                    variant="outlined"
                    sx={{ p: 3, textAlign: 'center' }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      Subscription details will be available after the team is created.
                    </Typography>
                  </Card>
                )}
              </TabPanel>
              {selectedTab !== "members" &&
                selectedTab !== "payout invoices" &&
                selectedTab !== "payout schedule" &&
                selectedTab !== "subscription" && (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {!isReadOnly && (
                      <>
                        <LoadingButton
                          size="large"
                          type="button"
                          variant="contained"
                          disabled={isReadOnly}
                          loading={isSubmitting || isLoading}
                          onClick={handleSaveClick}
                        >
                          Save
                        </LoadingButton>
                        <LoadingButton
                          size="large"
                          type="button"
                          variant="outlined"
                          component={RouterLink}
                          to="/team"
                        >
                          Cancel
                        </LoadingButton>
                      </>
                    )}
                  </Stack>
                )}
            </Form>
          </FormikProvider>
          {/* Payout schedule tab - separate from main form to avoid nesting */}
          <TabPanel value="payout schedule">
            {!isCreateTeam && (
              <TeamPayoutScheduleTab
                teamId={id}
                parentCompanyId={parentCompanyId}
                isReadOnly={isReadOnly}
              />
            )}
            {isCreateTeam && (
              <Card
                variant="outlined"
                sx={{ p: 3, textAlign: 'center' }}
              >
                <Typography variant="h6" color="text.secondary">
                  Payout schedule will be available after the team is created.
                </Typography>
              </Card>
            )}
          </TabPanel>
        </Card>
      </TabContext>
      <TeamPayoutAccountEditModal
        open={payoutAccountModalState.open}
        onClose={handlePayoutAccountEditClose}
        accountIndex={payoutAccountModalState.index}
        account={payoutAccountBeingEdited}
        onSave={handlePayoutAccountEditSave}
        businessTypes={possibleBusinessTypes}
        taxLabel={taxLabel}
        allAccounts={values.payoutAccounts || []}
      />
      {modalOpen && (
        <TeamUserModal
          open={modalOpen}
          teamId={id}
          onClose={handleMangageUsersClose}
          enableRedirection={false}
        />
      )}
      {imagePreview.open && (
        <ImgPreviewModal
          imageObj={imagePreview}
          buttonsArray={imagePreview.imagePreviewBtns}
          currentSavedImg={values?.attachment?.url}
        />
      )}

      {photoModal && (
        <Dialog
          onClose={() => {
            setPhotoModal({ open: false, src: "" });
          }}
          aria-labelledby="customized-dialog-title"
          open={photoModal.open}
        >
          <IconButton
            aria-label="close"
            onClick={() => {
              setPhotoModal({ open: false, src: "" });
            }}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            <GridCloseIcon />
          </IconButton>
          <DialogContent
            sx={{
              minHeight: "300px",
              minWidth: "600px",
              display: "flex",
              placeItems: "center",
              placeContent: "center",
              padding: "32px 40px",
            }}
          >
            <img src={photoModal.src} alt="Review" />
          </DialogContent>
        </Dialog>
      )}

      <SubscriptionDetailsModal
        open={subscriptionModalOpen}
        onClose={handleSubscriptionDetailsClose}
        subscriptionData={selectedSubscriptionData}
        teamId={id}
        onUpdate={() => {
          queryClient.invalidateQueries(queryKeys.team(id));
        }}
      />

      <BulkNotificationModal
        open={renewalNotificationModalOpen}
        onClose={() => { setRenewalNotificationModalOpen(false); setRenewalNotificationLicenseIds([]); setSelectedRenewalChargers([]); }}
        teamId={id}
        preSelectedLicenseIds={renewalNotificationLicenseIds}
      />


      {/* --- New license flow: modals --- */}
      <ChargerLicenseModal
        open={chargerLicensePurchaseModalOpen}
        onClose={() => setChargerLicensePurchaseModalOpen(false)}
        onConfirm={handleLicensePurchase}
        charger_license_payment_config={charger_license_payment_config}
        teamName={teams?.[0]?.name || ''}
        isOfflinePayment
      />

      <ChargerLicenseModal
        open={renewModalOpen}
        onClose={() => { setRenewModalOpen(false); setLicenseModalOpen(true); setSelectedRenewalChargers([]); }}
        onConfirm={handleLicensePurchase}
        charger_license_payment_config={charger_license_payment_config}
        teamName={teams?.[0]?.name || ''}
        chargersToRenew={selectedRenewalChargers}
        isOfflinePayment
      />

      {/* License Management Dialog */}
      <Dialog open={licenseModalOpen} onClose={handleLicenseCancel} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="eva:alert-triangle-fill" style={{ color: '#FF9800', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>License Management</Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
              <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'error.lighter', color: 'error.dark', fontSize: 12, fontWeight: 600 }}>
                {expiredLicenses} Expired
              </Box>
              <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'warning.lighter', color: 'warning.dark', fontSize: 12, fontWeight: 600 }}>
                {upcomingExpiringLicenses} Upcoming
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Select chargers to renew or view their details
          </Typography>
          <IconButton onClick={handleLicenseCancel} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
            <Icon icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 0, pt: 0 }}>
          {licenseDetailsFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon icon="eva:clock-outline" style={{ fontSize: 20, color: '#637381' }} />
                <Typography color="text.secondary">Loading license details...</Typography>
              </Box>
            </Box>
          ) : (
            <TabContext value={licenseSelectedTab}>
              <Box sx={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                <RootTabStyle indicatorColor="transparent" centered length={LICENSE_TABS.length} onChange={handleLicenseTabChange}>
                  {LICENSE_TABS.map((tab) => (
                    <RootTabItemStyles
                      key={tab.value}
                      value={tab.value}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {tab.label}
                          <Box component="span" sx={{
                            px: 0.75, borderRadius: 10, fontSize: 11, fontWeight: 700, lineHeight: '18px',
                            bgcolor: tab.value === 'expiredLicenses' ? 'error.main' : 'warning.main',
                            color: 'white',
                          }}>
                            {tab.value === 'expiredLicenses' ? expiredLicenses : upcomingExpiringLicenses}
                          </Box>
                        </Box>
                      }
                    />
                  ))}
                </RootTabStyle>
              </Box>
              <TabPanel value="expiredLicenses" sx={{ px: 2, pt: 1.5 }}>
                <RenewalChargerTable
                  rows={chargersWithoutLicense}
                  loading={licenseDetailsFetching}
                  onSelectionChange={setSelectedRenewalChargers}
                />
              </TabPanel>
              <TabPanel value="upcomingLicensesExpiry" sx={{ px: 2, pt: 1.5 }}>
                <RenewalChargerTable
                  rows={chargersWithUpcomingLicenseExpiry}
                  loading={licenseDetailsFetching}
                  onSelectionChange={setSelectedRenewalChargers}
                />
              </TabPanel>
            </TabContext>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleLicenseCancel} variant="outlined" color="inherit">Close</Button>
          {licenseSelectedTab === 'upcomingLicensesExpiry' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Icon icon="eva:bell-fill" />}
              disabled={selectedRenewalChargers.length === 0}
              onClick={() => {
                const licensePks = selectedRenewalChargers.filter(c => c.license_pk).map(c => c.license_pk);
                setRenewalNotificationLicenseIds(licensePks);
                handleLicenseModalClose();
                setRenewalNotificationModalOpen(true);
              }}
            >
              Send Notifications ({selectedRenewalChargers.length})
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            disabled={selectedRenewalChargers.length === 0}
            onClick={() => { handleLicenseModalClose(); setRenewModalOpen(true); }}
          >
            Renew Selected ({selectedRenewalChargers.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
